/**
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
import { ILicenceTemplate } from '@vamstar/fox-api-common/esm/licence-template/types';
import { DEFAULT_PAGE_SIZE, XGridTable } from '@vamstar/fox-frontend-common';
import React, { Component } from 'react';
import { compose } from 'recompose';
import { log } from '@vamstar/fox-node-logger';

import { columns } from './columns';
import { LICENCE_TEMPLATE } from './queries';
import { XGridTableWrapper } from '../../common/components/XGridTableWrapper';
import { WithCommonPagination, withCommonPagination } from '../../common/utils/WithCommonPagination';
import { XGridSearch } from '../../common/components/XGridSearch';

interface ILicenceTemplateTableState {
  licenceTemplateData: ILicenceTemplate[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  searchText: string;
}

type HOCProps = WithCommonPagination;

class LicenceTemplatesTableView extends Component<HOCProps, ILicenceTemplateTableState> {
  state: ILicenceTemplateTableState = {
    licenceTemplateData: [],
    isLoading: false,
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    searchText: '',
  };

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ isLoading: true });

    const { commonPaginationService } = this.props;
    const { pageSize, currentPage, searchText } = this.state;
    try {
      const skip = (currentPage - 1) * pageSize;
      const licenceTemplateResponse = await commonPaginationService.fetchPaginatedData<ILicenceTemplate>(
        LICENCE_TEMPLATE,
        skip,
        pageSize,
        searchText,
      );
      if (licenceTemplateResponse !== null) {
        const { items, total } = licenceTemplateResponse.licencetemplates;
        this.setState({ licenceTemplateData: items, totalCount: total, pageSize });
      }
    } catch (error) {
      log.error(JSON.stringify(error));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  onPageSizeChange = (newPageSize: number) => {
    this.setState({ pageSize: newPageSize, currentPage: 1 }, () => this.fetchData());
  };

  onPageChange = (page: number) => {
    this.setState({ currentPage: page }, () => this.fetchData());
  };

  onSearchChange = (searchText: string) => {
    this.setState({ searchText });
  };

  render() {
    const { isLoading, licenceTemplateData, totalCount, pageSize, currentPage, searchText } = this.state;
    return (
      <XGridTableWrapper
        heading="Licence Templates"
        route="/licence-template"
        buttonLabel="Add Licence Template"
        checkSuperAdmin
      >
        <XGridSearch
          value={searchText}
          onSearchSubmit={() => this.fetchData()}
          onSearchChange={(val) => this.onSearchChange(val)}
        />
        <XGridTable
          disableSelectionOnClick
          disableColumnFilter
          columns={columns}
          disableColumnResize
          rows={licenceTemplateData}
          autoHeight
          count={totalCount}
          loading={isLoading}
          onPageChange={this.onPageChange}
          onPageSizeChange={this.onPageSizeChange}
          page={currentPage}
          rowsPerPage={pageSize}
        />
      </XGridTableWrapper>
    );
  }
}

const LicenceTemplatesTable: React.ComponentClass<any, ILicenceTemplateTableState> = compose<HOCProps, any>(
  withCommonPagination,
)(LicenceTemplatesTableView);

export default LicenceTemplatesTable;
